/* eslint-disable react/jsx-no-undef */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import CardLayout from "../Cards/CardLayout";
import ReactECharts from "echarts-for-react";
import { useFetchData } from "../../customHooks/useGetDashboardData";
import Loader from "../feedBacks/loader";
import CardError from "../feedBacks/CardError";
import NoContent from "../feedBacks/NoContent";
import { COLORS, REFETCH_CONFIGURATION } from "../../utils/constants";
import { EChartsOption } from "echarts-for-react";
import { useEffect, useMemo, useState } from "react";
import NoDeviceCardError from "../feedBacks/NoDeviceCardError";
import { useUtils } from "../../customHooks/useUtils";

interface IPageVisit {
  page_url: string;
  total_visits: number;
}

function WebsiteVisitChart({user_id}: {user_id?: string}) {
  const { FEEDBACK_MESSAGES, t } = useUtils();
  const filterOptions = useMemo(
    () => [
      { key: "7 Days", value: 7 },
      { key: "2 Weeks", value: 14 },
      { key: "1 Month", value: 30 },
    ],
    []
  );

  const getDateRangeFromDays = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    return {
      start_date: start.toISOString().split("T")[0],
      end_date: end.toISOString().split("T")[0],
    };
  };

  const [days, setDays] = useState<number>(filterOptions[0].value);
  const { start_date, end_date } = getDateRangeFromDays(days);
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const {
    data: pageVisitsData,
    isLoading: isDataLoading,
    error,
    refetch,
  } = useFetchData<{ datasets: IPageVisit[] }>(
    ["/user-analytics", "fetchPageVisits", user_id],
    `/user-analytics/page-visits/`,
    {
      start_date,
      end_date,
      user_id,
    },
    // @ts-ignore
    {
      ...REFETCH_CONFIGURATION,
    }
  );

  // Transform API response into chart-friendly data
  const transformedData: IPageVisit[] = pageVisitsData?.datasets || [];

  const roleMappings: Record<string, string> = {
    admin: "Admin",
    finance: "Finance",
    operations: "Operations",
    "senior-manager": "Manager",
  };

  // Helper to map page_url into readable labels
  const mapPageUrlToLabel = (url: string): string => {
    for (const key in roleMappings) {
      if (url.includes(`/${key}`)) {
        return roleMappings[key];
      }
    }
    return "Unknown";
  };

  // Group by label and sum total_visits
  const groupedData = transformedData.reduce<Record<string, number>>(
    (acc, item) => {
      const label = mapPageUrlToLabel(item.page_url);
      acc[label] = (acc[label] || 0) + item.total_visits;
      return acc;
    },
    {}
  );
  const chartLabels = Object.keys(groupedData);
  const chartData = Object.values(groupedData);

  const isLoading = isDataLoading || isLocalLoading;

  useEffect(() => {
    if (start_date && end_date) {
      setIsLocalLoading(true);
      refetch().finally(() => {
        setIsLocalLoading(false);
      });
    }
  }, [start_date, end_date, refetch]);


  const maxVal = Math.max(...chartData, 0);
  const chartPalette = [
    COLORS.PRIMARY,
    COLORS.RED,
    COLORS.PURPLE,
    COLORS.GREEN,
    COLORS.NEUTRAL,
    COLORS.PHASE_A,
    COLORS.PHASE_B,
    COLORS.PHASE_C,
  ];

  const options: EChartsOption = {
    grid: {
      left: "5%",
      right: "5%",
      top: "10%",
      bottom: "15%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: chartLabels.length > 0 ? chartLabels : [FEEDBACK_MESSAGES.NO_DATA],
      axisLabel: {
        fontWeight: "bold",
        fontSize: 11,
        interval: 0,
        rotate: 45,
        overflow: "truncate",
      },
    },
    yAxis: {
      type: "value",
      max: "dataMax",
      axisLabel: {
        fontWeight: "bold",
        fontSize: 11,
      },
    },
    series: [
      {
        type: "bar",
        data: chartData.map(() => maxVal),
        barGap: "-100%",
        barWidth: "40%",
        itemStyle: {
          color: "#e0e0e0",
          //   borderRadius: [8, 8, 8, 8],
        },
        silent: true,
      },
      {
        name: t("chart.website_visits"),
        type: "bar",
        data: chartData.map((val, i) => ({
          value: val,
          itemStyle: {
            color: chartPalette[i % chartPalette.length],
            borderRadius: [8, 8, 0, 0],
          },
        })),
        barWidth: "40%",
        z: 10,
      },
    ],
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: function (params: any) {
        const data = params[1];
        return `<div style="font-size:10px">${data.value.toLocaleString()} visits</div>`;
      },
      textStyle: { fontSize: 10 },
    },
    animationDuration: 0,
    animationDurationUpdate: 3000,
    animationEasing: "linear",
    animationEasingUpdate: "linear",
  };
  return (
    <CardLayout
      title={'Page Visits'}
      className="lg:min-w-[570px] flex-1"
      besideTitleComponent={
        <div className="flex !w-full justify-end">
          <select
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            value={days}
            className="cursor-pointer flex items-center justify-center max-w-[30%] py-1 px-2 rounded-full bg-[#E5E5E5] text-primary-blackMain text-[10px]"
          >
            {filterOptions.map((item) => (
              <option
                key={item.key}
                value={item.value}
                className="text-primary-blackLight cursor-pointer"
              >
                {item.key}
              </option>
            ))}
          </select>
        </div>
      }
      besideComponentStyle="flex-1 !justify-end"
    >
      <div className="flex flex-col h-full w-full">
        {isLoading ? (
          <div className="flex justify-center items-center h-full w-full">
            <Loader />
          </div>
        ) : error ? (
          (error as any).response?.data?.detail ? (
            <NoDeviceCardError
              message={FEEDBACK_MESSAGES.NO_DEVICE}
              className="!max-h-[150px]"
            />
          ) : (
            <CardError
              message={FEEDBACK_MESSAGES.ERROR}
              className="!h-[100px]"
            />
          )
        ) : transformedData.length === 0 ? (
          <NoContent message={FEEDBACK_MESSAGES.NO_DATA} />
        ) : (
          <div className="h-full w-full relative p-0">
            <ReactECharts option={options} />
          </div>
        )}
      </div>
    </CardLayout>
  );
}

export default WebsiteVisitChart;
